import dayjs from "dayjs";
import { useAuthStore } from "../../../Stores/authStore";

export const TENDER_SEARCH_QUERY_OPTIMIZATIONS_FLAG = "tender_search_query_optimizations";
export const TENDER_SEARCH_OPTIMIZED_ORDERING_COLUMNS = ["publication_date", "closing_date", "id"];
export const TENDER_SEARCH_CLOSED_TENDERS_MAX_INTERVAL_MONTHS = 3;

export const validTenderColumns = [
  "platform_id",
  "tender_national_id",
  "estimated_total_value",
  "publication_date",
  "closing_date",
  "extension_date",
  "extension",
  "tender_url",
  "tendering_authority__name",
  "nut_code",
  "title",
  "procedure_type",
  "award_criteria",
  "currency",
  "id",
  "soa_categories",
  "confirmation_state",
  "extras_generation_state",
  "confirmed",
];

const validTenderColumnsSet = new Set(validTenderColumns);
const optimizedOrderingColumnsSet = new Set(TENDER_SEARCH_OPTIMIZED_ORDERING_COLUMNS);

export function hasTenderSearchQueryOptimizations(admin) {
  return !admin && Boolean(useAuthStore.getState()?.user?.flags?.[TENDER_SEARCH_QUERY_OPTIMIZATIONS_FLAG]);
}

export function getTenderSearchOrderingColumns(admin) {
  return hasTenderSearchQueryOptimizations(admin) ? TENDER_SEARCH_OPTIMIZED_ORDERING_COLUMNS : validTenderColumns;
}

export function isTenderSearchOrderingColumnAllowed(column, admin) {
  if (!column) {
    return false;
  }
  const orderingKey = column.replace(/^-/, "");
  return hasTenderSearchQueryOptimizations(admin)
    ? optimizedOrderingColumnsSet.has(orderingKey)
    : validTenderColumnsSet.has(orderingKey);
}

export function getOptimizedClosedTenderDateRange() {
  const end = dayjs();
  return {
    start: end.subtract(TENDER_SEARCH_CLOSED_TENDERS_MAX_INTERVAL_MONTHS, "month").format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
  };
}

export function isSearchClosedTendersEnabled(value) {
  return value === true || value === "true";
}

export function normalizeOptimizedClosingDateRange(params) {
  let closingStart = params.closing_date_start || "";
  let closingEnd = params.closing_date_end || "";

  if (closingStart && !dayjs(closingStart).isValid()) {
    closingStart = "";
  }
  if (closingEnd && !dayjs(closingEnd).isValid()) {
    closingEnd = "";
  }

  return {
    ...params,
    closing_date_start: closingStart,
    closing_date_end: closingEnd,
  };
}

export function normalizeTenderSearchTableParams(params, admin) {
  const sanitizeColumns = (columns) => (columns || []).filter((column) => validTenderColumnsSet.has(column));
  const orderingKey =
    typeof params.orderingURL === "string" ? params.orderingURL.replace(/^-/, "") : params.orderingTable?.id;
  const tableOrderingIsDescending = params.orderingTable?.desc === true || params.orderingTable?.desc === "true";
  const orderingIsDescending =
    typeof params.orderingURL === "string" ? params.orderingURL.startsWith("-") : tableOrderingIsDescending;

  const normalized = {
    ...params,
    filterColumns: sanitizeColumns(params.filterColumns),
    searchColumns: sanitizeColumns(params.searchColumns),
  };

  if (isTenderSearchOrderingColumnAllowed(params.orderingTable?.id, admin)) {
    normalized.orderingTable = { id: params.orderingTable.id, desc: orderingIsDescending };
  } else if (isTenderSearchOrderingColumnAllowed(orderingKey, admin)) {
    normalized.orderingTable = { id: orderingKey, desc: orderingIsDescending };
  } else {
    normalized.orderingTable = { id: "publication_date", desc: true };
  }

  if (typeof params.orderingURL === "string" && isTenderSearchOrderingColumnAllowed(params.orderingURL, admin)) {
    normalized.orderingURL = params.orderingURL;
  } else if (isTenderSearchOrderingColumnAllowed(params.orderingTable?.id, admin)) {
    normalized.orderingURL = `${orderingIsDescending ? "-" : ""}${params.orderingTable.id}`;
  } else {
    normalized.orderingURL = "-publication_date";
  }

  if (!hasTenderSearchQueryOptimizations(admin)) {
    return normalized;
  }

  if (!isSearchClosedTendersEnabled(normalized.search_closed_tenders)) {
    return {
      ...normalized,
      search_closed_tenders: false,
      closing_date_start: "",
      closing_date_end: "",
      no_closing_date: "",
    };
  }

  return normalizeOptimizedClosingDateRange({
    ...normalized,
    search_closed_tenders: true,
    no_closing_date: "",
  });
}

export function getTenderSearchClosingParams(tableParams, admin) {
  if (!hasTenderSearchQueryOptimizations(admin)) {
    const noClosingDate = tableParams.no_closing_date;
    return {
      closingStart: noClosingDate ? "" : tableParams.closing_date_start,
      closingEnd: noClosingDate ? "" : tableParams.closing_date_end,
      noClosingDate,
    };
  }

  if (!isSearchClosedTendersEnabled(tableParams.search_closed_tenders)) {
    return {
      closingStart: "",
      closingEnd: "",
      noClosingDate: "",
    };
  }

  const normalized = normalizeOptimizedClosingDateRange(tableParams);
  return {
    closingStart: normalized.closing_date_start,
    closingEnd: normalized.closing_date_end,
    noClosingDate: "",
  };
}

export function appendOptimizedTenderSearchParams(params, tableParams, admin) {
  if (!hasTenderSearchQueryOptimizations(admin)) {
    return;
  }

  const normalized = normalizeTenderSearchTableParams(tableParams, admin);
  params.delete("page");
  params.set("ordering", normalized.orderingURL);

  if (normalized.search_closed_tenders) {
    params.set("search_closed_tenders", "true");
    if (normalized.closing_date_start) {
      params.set("closing_after", normalized.closing_date_start);
    } else {
      params.delete("closing_after");
    }
    if (normalized.closing_date_end) {
      params.set("closing_before", normalized.closing_date_end);
    } else {
      params.delete("closing_before");
    }
  } else {
    params.delete("search_closed_tenders");
    params.delete("closing_after");
    params.delete("closing_before");
    params.delete("no_closing_date");
  }
}

export function buildTenderSearchUrl({
  urlPrefix,
  tab,
  tenderStrategies,
  tableParamRef,
  admin,
  pageNum,
  cursorUrl,
}) {
  if (hasTenderSearchQueryOptimizations(admin) && cursorUrl) {
    return cursorUrl;
  }

  const normalizedParams = normalizeTenderSearchTableParams(tableParamRef.current[tab], admin);
  tableParamRef.current[tab] = normalizedParams;

  const params = new URLSearchParams();
  if (!hasTenderSearchQueryOptimizations(admin)) {
    params.set("page", pageNum || normalizedParams.page || 1);
  }
  if (tab !== 0) {
    params.set("strategy_id", tenderStrategies[tab].id);
  }
  params.set("page_size", normalizedParams.page_size);

  if (admin) {
    params.set("confirmed", normalizedParams.confirmed);
    params.set(
      "confirmation_state",
      normalizedParams.confirmation_state === "all" ? "" : normalizedParams.confirmation_state
    );
    if (normalizedParams.extras_generation_state) {
      params.set("extras_generation_state", normalizedParams.extras_generation_state);
    }
    if (normalizedParams.notice_type?.length > 0) {
      normalizedParams.notice_type.forEach((noticeType) => {
        params.append("notice_type", noticeType.value);
      });
    }
    if (normalizedParams.has_attachments === true || normalizedParams.has_attachments === "true") {
      params.set("has_attachments", "true");
    }
  }

  const ordering =
    Object.keys(tenderStrategies)[tab] === "0" && normalizedParams.orderingURL === "product_count"
      ? ""
      : normalizedParams.orderingURL;
  const useTestSet = Object.keys(tenderStrategies)[tab] === "0" ? "" : normalizedParams.use_test_set;

  params.set("ordering", ordering);
  params.set("hide_disliked", normalizedParams.hide_disliked ?? "");
  params.set("use_test_set", useTestSet ?? "");
  params.set("min_value", normalizedParams.min_value ?? "");
  params.set("max_value", normalizedParams.max_value ?? "");
  params.set("search", normalizedParams.search ?? "");
  params.set("nut", normalizedParams.nut ?? "");

  const closingParams = getTenderSearchClosingParams(normalizedParams, admin);
  params.set("closing_after", closingParams.closingStart ?? "");
  params.set("closing_before", closingParams.closingEnd ?? "");
  params.set("publication_after", normalizedParams.publication_date_start ?? "");
  params.set("publication_before", normalizedParams.publication_date_end ?? "");
  params.set("no_closing_date", closingParams.noClosingDate ?? "");

  if (hasTenderSearchQueryOptimizations(admin)) {
    appendOptimizedTenderSearchParams(params, normalizedParams, admin);
  }

  return `${urlPrefix}?${params.toString()}`;
}

export function buildTenderSearchUrlFromSearchParams({ urlPrefix, searchParams, admin }) {
  const params = new URLSearchParams(searchParams.toString());

  if (hasTenderSearchQueryOptimizations(admin)) {
    const ordering = params.get("ordering");
    if (!isTenderSearchOrderingColumnAllowed(ordering, admin)) {
      params.set("ordering", "-publication_date");
    }
    const normalized = normalizeTenderSearchTableParams(
      {
        orderingURL: params.get("ordering"),
        closing_date_start: params.get("closing_after") || "",
        closing_date_end: params.get("closing_before") || "",
        search_closed_tenders: params.get("search_closed_tenders") || false,
      },
      admin
    );
    appendOptimizedTenderSearchParams(params, normalized, admin);
  }

  return `${urlPrefix}?${params.toString()}`;
}
